import React, { useEffect, useState } from "react";
import {
  getSharedRecorder,
  type RecorderState,
  type StopResult,
} from "../../lib/voiceoverRecorder";

type Props = {
  /** Aktuelle Player-Position (Sek) — wird als `startedAtPlayerSec` gespeichert,
   *  damit das Resultat beim Stop genau an dieser Stelle als VO-Segment landet. */
  playheadSec: number;
  /** Wenn true, startet die Vorschau gleichzeitig mit der Aufnahme. Ist
   *  default empfohlen damit der User Lip-Syncen kann; abschaltbar falls
   *  jemand bewusst nur die Stimme ohne Vorschau-Audio aufnehmen will. */
  autoPlayWhilstRecording: boolean;
  onAutoPlayToggle: (next: boolean) => void;
  /** Composer-Hooks — die Vorschau zu starten/stoppen ohne Player-Remount. */
  onPlayPreview: () => void;
  onPausePreview: () => void;
  /** Composer kümmert sich um Decode + Encode + Upload + Segment-Placement. */
  onRecordingFinished: (res: StopResult) => Promise<void> | void;
  /** Optional Hint, falls der User noch keine VO-Spur hat (Recording legt
   *  beim Finishen automatisch eine an, dann ist der Hint nicht mehr nötig). */
  hasExistingVoiceover: boolean;
};

/**
 * UI-Komponente für die Mikrofon-Aufnahme. Sitzt im VoiceoverPanel und
 * zeigt Status (idle / recording / stopping), Pegel-Meter, Timer + Aktion-
 * Buttons. Die Aufnahme selbst läuft im `VoiceoverRecorder`-Singleton, das
 * UI subscribt nur auf den Status.
 */
export const MicRecorder: React.FC<Props> = ({
  playheadSec,
  autoPlayWhilstRecording,
  onAutoPlayToggle,
  onPlayPreview,
  onPausePreview,
  onRecordingFinished,
  hasExistingVoiceover,
}) => {
  const [state, setState] = useState<RecorderState>(() => getSharedRecorder().getState());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const rec = getSharedRecorder();
    return rec.subscribe(setState);
  }, []);

  const onStart = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const rec = getSharedRecorder();
      await rec.start({ playerStartSec: playheadSec });
      if (autoPlayWhilstRecording) {
        // Nach `start()` ist das Mic stream-mäßig aktiv. Erst dann den
        // Player triggern, sodass der User exakt sieht/hört, was er
        // gerade aufnimmt.
        try {
          onPlayPreview();
        } catch { /* ignore */ }
      }
    } catch {
      // Fehler steckt schon im RecorderState.
    } finally {
      setBusy(false);
    }
  };

  const onStop = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const rec = getSharedRecorder();
      const result = await rec.stop();
      try { onPausePreview(); } catch { /* ignore */ }
      if (result) {
        await onRecordingFinished(result);
      }
    } finally {
      setBusy(false);
    }
  };

  const onCancel = (): void => {
    getSharedRecorder().cancel();
    try { onPausePreview(); } catch { /* ignore */ }
  };

  const isActive = state.status === "recording" || state.status === "starting" || state.status === "stopping";

  return (
    <div className={`mic-recorder ${isActive ? "is-active" : ""}`}>
      <div className="mic-recorder-head">
        <span className="mic-recorder-title">🎙️ Mikrofon-Aufnahme</span>
        <span className={`mic-recorder-status status-${state.status}`}>
          {state.status === "idle" && "Bereit"}
          {state.status === "starting" && "Starte…"}
          {state.status === "recording" && (
            <>
              <span className="mic-rec-dot" /> Läuft · {state.elapsedSec.toFixed(1)}s
            </>
          )}
          {state.status === "stopping" && "Stoppe…"}
          {state.status === "error" && "Fehler"}
        </span>
      </div>

      <div className="mic-level-wrap" aria-hidden>
        <div
          className="mic-level-bar"
          style={{ width: `${Math.round(Math.min(1, state.level) * 100)}%` }}
        />
        {/* Skala-Markierungen alle 10% — gibt visuelles Feedback, ob der
         *  User zu leise oder ins Clipping spricht. */}
        {Array.from({ length: 9 }).map((_, i) => (
          <span
            key={i}
            className={`mic-level-tick ${i >= 7 ? "warn" : ""}`}
            style={{ left: `${(i + 1) * 10}%` }}
          />
        ))}
      </div>

      <div className="mic-recorder-actions">
        {state.status !== "recording" && state.status !== "stopping" ? (
          <button
            type="button"
            className="btn primary sm mic-rec-btn"
            disabled={busy || state.status === "starting"}
            onClick={() => void onStart()}
          >
            ● Aufnahme starten
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn danger sm mic-rec-btn"
              disabled={busy || state.status === "stopping"}
              onClick={() => void onStop()}
            >
              ◼ Stoppen + Speichern
            </button>
            <button
              type="button"
              className="btn ghost sm"
              disabled={busy}
              onClick={onCancel}
              title="Aufnahme abbrechen — nichts wird gespeichert"
            >
              ✕ Abbrechen
            </button>
          </>
        )}
      </div>

      <label className="mic-recorder-option">
        <input
          type="checkbox"
          checked={autoPlayWhilstRecording}
          onChange={(e) => onAutoPlayToggle(e.target.checked)}
        />
        <span>
          Während der Aufnahme die Vorschau abspielen (Lip-Sync) — Headset
          empfohlen, damit das Mic deine Stimme sauber ohne Reel-Audio
          erfasst.
        </span>
      </label>

      <div className="mic-recorder-hint">
        {state.status === "error" ? (
          <span className="muted err">{state.error ?? "Mic-Zugriff fehlgeschlagen."}</span>
        ) : state.status === "recording" ? (
          <span className="muted">
            Aufnahme startet bei <strong>{state.startedAtPlayerSec.toFixed(2)}s</strong> auf der
            Timeline. Stoppen → wird automatisch als VO-Segment platziert.
          </span>
        ) : hasExistingVoiceover ? (
          <span className="muted">
            Tipp: Aufnahme wird als zusätzliches Segment auf die bestehende
            Voiceover-Spur gelegt — du kannst beliebig viele Takes mischen.
          </span>
        ) : (
          <span className="muted">
            Tipp: Playhead positionieren → Aufnahme starten. Beim Stoppen
            erscheint die Aufnahme als VO-Segment exakt an dieser Position.
          </span>
        )}
      </div>
    </div>
  );
};
